import { TFunction } from 'i18next';
import { Component, ReactNode } from 'react';
import { Field } from 'redux-form';
import { DefaultTheme, withTheme } from 'styled-components';

import { CountryCode, POSTAL_CODE_PRIMARY_COUNTRIES, US_STATES } from 'lib/constants/locales';
import { POSTAL_CODE_GB_REGEX, POSTAL_CODE_REGEX } from 'lib/constants/regexes';
import { InputStyles } from 'lib/interfaces/input-styles';
import PiiAttributes from 'lib/interfaces/PiiAttributes';
import PiiAttributeValues from 'lib/interfaces/PiiAttributeValues';
import { UsStateInputMethod } from 'lib/interfaces/ThemeSerialized';
import { AddressAutocompleteMethod } from 'lib/interfaces/verificationConfig';
import {
  isMaxLength,
  isRequired,
  matchesRegexp,
  Rule,
  withMessage,
} from 'lib/utils/formValidation';
import ThemedFormSelect from 'verify/components/FormControls/ThemedFormSelect';
import FormGroup from 'verify/components/FormGroup';
import LocationSearchInput from 'verify/components/LocationSearchInput';
import ThemedFormInput from 'verify/components/ThemedFormInput';

const validate: Record<string, Rule | Rule[]> = {
  autocompleteClosed: [
    // This is used to flag validation errors when the autocomplete isn't
    // expanded. The fields with the actual validation functions don't
    // exist when not expanded so their validators won't run.
    // This won't actually be shown to the user.
    withMessage('Fill out the details of your current address', () => false),
  ],

  addressStreet1: [
    // 80 char is the small business admin (SBA) street length limit
    withMessage('Try a shorter street address format', isMaxLength(80)),
    withMessage('Your current street address is required', isRequired),
  ],

  // International validation
  addressCityI18n: [],
  addressPostalCode: [],
  addressSubdivision: [],

  addressPostalCodeGB: [
    withMessage('Please enter your full postal code', isRequired),
    withMessage('Please enter your full postal code', matchesRegexp(POSTAL_CODE_GB_REGEX)),
  ],

  // US validation
  addressCityUs: withMessage('The city of your current address is required', isRequired),
  addressState: withMessage('The state of your current address is required', isRequired),
  addressZipCode: [
    withMessage('Enter your current ZIP or postal code to verify your address', isRequired),
    withMessage('The ZIP code you entered is invalid', matchesRegexp(POSTAL_CODE_REGEX)),
  ],
};

interface AddressInputSectionProps {
  boundChangeFormValue: (field: string, value: string) => void;
  selectedCountryCode: CountryCode;
  disabled?: boolean;
  forceOpen: boolean;
  latLng: google.maps.LatLngLiteral;
  lockedAttributes: PiiAttributeValues;
  t: TFunction;
  theme: DefaultTheme;
  addressAutocompleteMethod: AddressAutocompleteMethod;
}

interface AddressInputSectionState {
  manualInputOpen: boolean;
}

class AddressInputSection extends Component<AddressInputSectionProps, AddressInputSectionState> {
  state: AddressInputSectionState = {
    // google maps silently fails to load on older unsupported versions of Safari
    // set input address to be autoexpanded on initial render if places is not defined
    // it should still be safe to render LocationSearchInput.
    // We tested setting window.google.maps.places to undefined and autocomplete just silently fails
    // We also tested loading google maps api after input address rendered, and autocomplete
    // does work after google maps api is loaded succesfully, so we don't really have a race condition
    // if input address is on the first screen
    // (in practice, google maps api almost always loads before the inquiry anyways)
    manualInputOpen: !window.google?.maps?.places,
  };

  public render() {
    const {
      t,
      theme: { inputStyle },
    } = this.props;

    return (
      <FormGroup>
        {inputStyle === InputStyles.Outlined && (
          <label htmlFor={PiiAttributes.AddressStreet1}>{t('form.address.label')}</label>
        )}
        {this.renderAddressFields()}
      </FormGroup>
    );
  }

  private renderAddressFields() {
    const {
      selectedCountryCode,
      disabled,
      forceOpen,
      t,
      theme: { inputStyle },
      addressAutocompleteMethod,
    } = this.props;

    if (
      this.state.manualInputOpen ||
      forceOpen ||
      addressAutocompleteMethod === AddressAutocompleteMethod.None
    ) {
      return this.renderCountryOrderedAddress();
    } else {
      const placeholder =
        inputStyle === InputStyles.Outlined
          ? t('form.address.placeholder', { context: selectedCountryCode })
          : t('form.address.label');

      return (
        <div>
          <Field
            selectedCountryCode={selectedCountryCode}
            name={this.getAutocompleteInputName()}
            boundChangeFormValue={this.handleAddressChange}
            component={LocationSearchInput}
            disabled={disabled}
            type='text'
            validate={validate.autocompleteClosed}
            latLng={this.props.latLng}
            placeholder={placeholder}
          />
          <a
            id='address_input_section__manual_edit'
            data-test='address_input_section__manual_edit'
            onClick={this.handleOpenManualInput}
            tabIndex={0}
          >
            {t('form.address.edit-address-manually')}
          </a>
        </div>
      );
    }
  }

  private handleAddressChange = (field: string, value: string) => {
    this.props.boundChangeFormValue(field, value);
    this.setState({ manualInputOpen: true });
  };

  private handleOpenManualInput = () => {
    this.setState({ manualInputOpen: true });
  };

  private renderCountryOrderedAddress = () => {
    const { selectedCountryCode, disabled, lockedAttributes, t, theme } = this.props;

    const street2Field = (
      <Field
        autoComplete='address-line2'
        name='address-street-2'
        component={ThemedFormInput}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        disabled={disabled || lockedAttributes?.[PiiAttributes.AddressStreet1]}
        type='text'
        placeholder={t('form.address.street-2')}
      />
    );

    const cityField = (
      <Field
        autoComplete='address-level2'
        name={PiiAttributes.AddressCity}
        component={ThemedFormInput}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        disabled={disabled || lockedAttributes?.[PiiAttributes.AddressCity]}
        type='text'
        placeholder={t('form.address.city')}
        validate={selectedCountryCode === 'US' ? validate.addressCityUs : validate.addressCityI18n}
      />
    );
    const provinceField = (
      <ProvinceFormField
        selectedCountryCode={selectedCountryCode}
        disabled={disabled || !!lockedAttributes?.[PiiAttributes.AddressSubdivision]}
        t={t}
        theme={theme}
      />
    );

    const rows = this.getFieldOrder(
      selectedCountryCode,
      this.getStreet1Field(),
      street2Field,
      cityField,
      provinceField,
      this.getPostalCodeField(),
    );

    return rows.map((row, rowIndex) => (
      <div key={rowIndex} className='row'>
        {row.map((field, index) => (
          <div key={index} className='col-sm-12'>
            {field}
          </div>
        ))}
      </div>
    ));
  };

  private getStreet1Field() {
    const { addressAutocompleteMethod, selectedCountryCode, disabled, lockedAttributes, t } =
      this.props;

    if (
      this.getAutocompleteInputName() === PiiAttributes.AddressStreet1 &&
      addressAutocompleteMethod === AddressAutocompleteMethod.GoogleMaps
    ) {
      return (
        <Field
          autoComplete='address-line1'
          selectedCountryCode={selectedCountryCode}
          name={PiiAttributes.AddressStreet1}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          disabled={
            disabled || (lockedAttributes && lockedAttributes[PiiAttributes.AddressStreet1])
          }
          placeholder={t('form.address.street-1')}
          type='text'
          validate={validate.addressStreet1}
          boundChangeFormValue={this.props.boundChangeFormValue}
          component={LocationSearchInput}
          latLng={this.props.latLng}
        />
      );
    } else {
      return (
        <Field
          autoComplete='address-line1'
          name={PiiAttributes.AddressStreet1}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          disabled={
            disabled || (lockedAttributes && lockedAttributes[PiiAttributes.AddressStreet1])
          }
          placeholder={t('form.address.street-1')}
          type='text'
          validate={validate.addressStreet1}
          component={ThemedFormInput}
        />
      );
    }
  }

  /**
   * Returns the postal code input as an autocomplete search input for postal code primary countries.
   */
  private getPostalCodeField() {
    const { selectedCountryCode, disabled, lockedAttributes, t } = this.props;

    let postalCodeValidation;
    switch (selectedCountryCode) {
      case 'US':
        postalCodeValidation = validate.addressZipCode;
        break;

      case 'GB':
        postalCodeValidation = validate.addressPostalCodeGB;
        break;

      default:
        postalCodeValidation = validate.addressPostalCode;
        break;
    }

    if (this.getAutocompleteInputName() === PiiAttributes.AddressPostalCode) {
      return (
        <Field
          autoComplete='postal-code'
          selectedCountryCode={selectedCountryCode}
          name={PiiAttributes.AddressPostalCode}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          disabled={
            disabled || (lockedAttributes && lockedAttributes[PiiAttributes.AddressPostalCode])
          }
          placeholder={t('form.address.postal-code')}
          type='text'
          validate={postalCodeValidation}
          boundChangeFormValue={this.props.boundChangeFormValue}
          component={LocationSearchInput}
          latLng={this.props.latLng}
        />
      );
    } else {
      return (
        <Field
          autoComplete='postal-code'
          name={PiiAttributes.AddressPostalCode}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          disabled={
            disabled || (lockedAttributes && lockedAttributes[PiiAttributes.AddressPostalCode])
          }
          placeholder={t('form.address.postal-code')}
          type='text'
          validate={postalCodeValidation}
          boundChangeFormValue={this.props.boundChangeFormValue}
          component={ThemedFormInput}
        />
      );
    }
  }

  private getFieldOrder(
    selectedCountryCode: CountryCode,
    street1Field: ReactNode,
    street2Field: ReactNode,
    cityField: ReactNode,
    provinceField: ReactNode,
    postalCodeField: ReactNode,
  ): Array<Array<ReactNode>> {
    // Address formats are from http://bitboost.com/ref/international-address-formats.html

    switch (selectedCountryCode) {
      case 'AR':
      case 'GL':
      case 'IS':
        return [[street1Field], [street2Field], [postalCodeField, cityField]];

      case 'AT':
      case 'BE':
      case 'DK':
        return [
          [street1Field, street2Field],
          [postalCodeField, cityField],
        ];

      case 'BR':
      case 'CR':
        return [[street1Field, street2Field], [cityField, provinceField], [postalCodeField]];

      case 'CA':
        return [
          [street2Field, street1Field],
          [cityField, provinceField, postalCodeField],
        ];

      case 'CN':
        return [[street2Field], [street1Field, cityField], [provinceField, postalCodeField]];

      case 'IT':
        return [
          [street1Field, street2Field],
          [postalCodeField, cityField, provinceField],
        ];

      // Note that while GB is postal code primary, we still render it as the
      // last field after autocompletion. This is what Amazon UK does.
      case 'AU':
      case 'GB':
      case 'PH':
      case 'US':
        return [
          [street1Field, street2Field],
          [cityField, provinceField, postalCodeField],
        ];

      case 'FI':
      case 'FR':
      case 'DE':
      case 'IL':
        return [
          [street1Field, street2Field],
          [postalCodeField, cityField],
        ];

      case 'JP':
      case 'KR':
        return [
          [street1Field, street2Field],
          [cityField, postalCodeField],
        ];

      case 'IN':
        return [[street2Field], [street1Field], [cityField, postalCodeField]];

      case 'FJ':
        return [[street1Field, street2Field], [cityField]];

      case 'HK':
        return [[street2Field], [street1Field], [cityField]];

      case 'EE':
      case 'CZ':
      case 'TW':
      case 'ID':
      default:
        return [
          [street1Field, street2Field],
          [cityField, postalCodeField],
        ];
    }
  }

  private getAutocompleteInputName() {
    return POSTAL_CODE_PRIMARY_COUNTRIES.includes(this.props.selectedCountryCode)
      ? PiiAttributes.AddressPostalCode
      : PiiAttributes.AddressStreet1;
  }
}

export default withTheme(AddressInputSection);

function ProvinceFormField({
  selectedCountryCode,
  disabled,
  t,
  theme,
}: {
  selectedCountryCode: CountryCode;
  disabled: boolean;
  t: (key: string) => string;
  theme: DefaultTheme;
}) {
  if (selectedCountryCode === 'US' && theme.usStateInputMethod === UsStateInputMethod.Select) {
    return (
      <Field
        autoComplete='address-level1'
        name={PiiAttributes.AddressSubdivision}
        component={ThemedFormSelect}
        disabled={disabled}
        type='text'
        placeholder={t('form.address.province-us')}
        validate={validate.addressState}
      >
        <option value='' selected disabled>
          {t('form.address.province-us')}
        </option>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {Object.keys(US_STATES).map((code: keyof typeof US_STATES) => (
          // NOTE: this isn't translated, but refers to US states only
          <option key={code} value={US_STATES[code]}>
            {code}
          </option>
        ))}
      </Field>
    );
  } else {
    return (
      <Field
        autoComplete='address-level1'
        name={PiiAttributes.AddressSubdivision}
        component={ThemedFormInput}
        disabled={disabled}
        type='text'
        placeholder={
          selectedCountryCode === 'US' ? t('form.address.province-us') : t('form.address.province')
        }
        validate={
          selectedCountryCode === 'US' ? validate.addressState : validate.addressSubdivision
        }
      />
    );
  }
}
